import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { IconButton, InputBase, TablePagination } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { makeStyles } from "@mui/styles";
import _ from "lodash";
import React, { ChangeEvent, useEffect, useState } from "react";
import { ITableColumn, ITablePagination } from "../../Typings/Table";
import ListSkeleton from "../Skeleton/ListSkeleton";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "98%",
    background: "#F7F7F7",
  },
  table: {
    width: "100%",
    height: "100%",
    maxHeight: "70vh",
    [theme.breakpoints.down("md")]: {
      maxHeight: "60vh",
    },
  },
  search: {
    position: "relative",
    paddingLeft: "5px",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "rgba(0,0,0,.1)",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,.2)",
    },
    marginBottom: "20px",
    width: "20%",
    "&.Mui-focused": {
      width: "30%",
      transition: "width 0.3s",
    },
    transition: "width 0.3s",
    [theme.breakpoints.down("sm")]: {
      width: "98%",
      "&.Mui-focused": {
        width: "98%",
        transition: "width 0.3s",
      },
    },
  },
}));

interface ISortableTableProps {
  columns: ITableColumn[];
  tableData: any[];
  rowComponent: React.FunctionComponent<any>;
  loading: boolean;
  hideSearch: boolean;
  defaultSortIndex: number;
  setTableData: any;
  reverse: boolean;
  customProps: any;
  pagination?: ITablePagination;
}

export default function SortableTable({
  columns,
  tableData,
  rowComponent,
  loading,
  hideSearch,
  defaultSortIndex,
  setTableData,
  reverse,
  customProps,
  pagination = {} as ITablePagination,
}: ISortableTableProps) {
  const [data, setData] = useState(tableData);
  const [filteredData, setFilteredData] = useState(tableData);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortedProperty, setSortedProperty] = useState<string>();
  const [ascending, setAscending] = useState(reverse);
  const classes = useStyles();


  const RowComponent = rowComponent;

  function sortTable(column: ITableColumn) {
    if (column.propName === sortedProperty) {
      sortBy(column.propName, ascending, false);
      setAscending(!ascending);
    } else {
      setSortedProperty(column.propName);
      sortBy(column.propName, true, false);
      setAscending(false);
    }
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function compareByAlt(key: any, ascending: any) {
    return function (a: any, b: any) {
      const aValue = _.get(a, key);
      const bValue = _.get(b, key);

      // Check if the values are date strings
      const aDate = new Date(aValue);
      const bDate = new Date(bValue);
      const isDateComparison = !isNaN(aDate.getTime()) && !isNaN(bDate.getTime());

      let comparisonResult = 0;

      if (isDateComparison) {
        comparisonResult = aDate.getTime() - bDate.getTime();
      } else if (typeof aValue === "string" && typeof bValue === "string") {
        if (aValue.toLowerCase() < bValue.toLowerCase()) comparisonResult = -1;
        if (aValue.toLowerCase() > bValue.toLowerCase()) comparisonResult = 1;
      } else {
        if (aValue < bValue) comparisonResult = -1;
        if (aValue > bValue) comparisonResult = 1;
      }

      return ascending ? comparisonResult : -comparisonResult;
    };
  }

  function sortBy(key: any, ascending: any, firstLoad: boolean) {
    let arrayCopy = [...data];
    if (firstLoad === false) {
      arrayCopy = [...filteredData];
    }
    arrayCopy.sort(compareByAlt(key, ascending));
    setFilteredData(arrayCopy);
  }

  function handleChange(event: any) {
    let searchValue = event.target.value.toLowerCase();
    let result = [];
    result = searchTableForValue(searchValue);
    result.sort(compareByAlt(sortedProperty, !ascending));
    setFilteredData(result);
  }

  function searchTableForValue(searchValue: string) {
    return data.filter((object) =>
      Object.keys(object).some((key) => {
        for (var i in columns) {
          var elem = _.get(object, columns[i].propName);
          var elemDateCheck = new Date(elem).getTime();
          // Check if table element is a date, if so convert to localized string
          if (!isNaN(elemDateCheck)) {
            elem = new Date(elem).toLocaleDateString("en-US");
          }
          // Convert all non-string table elements to be strings
          if (typeof elem !== "string") {
            elem = String(elem);
          }
          if (elem?.toLowerCase().includes(searchValue)) {
            return object[key];
          }
        }
        return false;
      })
    );
  }

  useEffect(() => {
    setData(tableData);
    setFilteredData(tableData);
    setSortedProperty(columns[0].propName);
  }, [tableData, columns]);

  useEffect(() => {
    if (defaultSortIndex !== undefined) {
      sortBy(columns[defaultSortIndex].propName, reverse ? false : true, true);
    }
  }, [data, columns]);

  return (
    <>
      {!hideSearch ? (
        <InputBase className={classes.search} placeholder="Search..." onChange={handleChange} />
      ) : null}
      <TableContainer className={classes.table} component={Paper}>
        {loading ? (
          <ListSkeleton numRows={10} />
        ) : (
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column, i) => (
                  <TableCell
                    key={`${column.propName}-${i}`}
                    sx={{
                      backgroundColor: "#f0f0f0",
                      p: "0px 16px",
                    }}
                    align="left"
                    onClick={() => sortTable(column)}
                    style={{ fontWeight: "bold" }}
                  >
                    {column.label}
                    {column.propName !== "" ? (
                      <IconButton size="large">
                        {sortedProperty === column.propName && ascending === true ? (
                          <ArrowDownward />
                        ) : (
                          <ArrowUpward />
                        )}
                      </IconButton>
                    ) : null}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagination.enabled
                ? filteredData
                    .map((elem, index) => {
                      return (
                        <RowComponent
                          columns={columns}
                          element={elem}
                          key={index}
                          index={index}
                          setTableData={setTableData}
                          customProps={customProps}
                        />
                      );
                    })
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : filteredData.map((elem, index) => {
                    return (
                      <RowComponent
                        columns={columns}
                        element={elem}
                        key={index}
                        index={index}
                        setTableData={setTableData}
                        customProps={customProps}
                      />
                    );
                  })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
      {pagination.enabled && (
        <TablePagination
          rowsPerPageOptions={pagination.rowsPerPage}
          component="div"
          count={tableData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          data-test="tablePagination"
        />
      )}
    </>
  );
}
